/**
 * Town Portal Effect - Visual portal that teleports to town or back
 */

export type PortalType = 'town' | 'return';

export interface PortalState {
  x: number;
  y: number;
  active: boolean;
  animationTime: number;
  destination: string;
  type: PortalType;
}

export class TownPortal {
  private townPortal: PortalState;
  private returnPortal: PortalState;
  private readonly PORTAL_RADIUS = 40;
  private readonly ANIMATION_SPEED = 2;

  constructor() {
    this.townPortal = {
      x: 0,
      y: 0,
      active: false,
      animationTime: 0,
      destination: '',
      type: 'town',
    };
    this.returnPortal = {
      x: 0,
      y: 0,
      active: false,
      animationTime: 0,
      destination: '',
      type: 'return',
    };
  }

  openTownPortal(x: number, y: number, destination: string): void {
    this.townPortal = {
      x,
      y,
      active: true,
      animationTime: 0,
      destination,
      type: 'town',
    };
  }

  openReturnPortal(x: number, y: number, destination: string): void {
    this.returnPortal = {
      x,
      y,
      active: true,
      animationTime: 0,
      destination,
      type: 'return',
    };
  }

  closeTownPortal(): void {
    this.townPortal.active = false;
  }

  closeReturnPortal(): void {
    this.returnPortal.active = false;
  }

  isTownPortalActive(): boolean {
    return this.townPortal.active;
  }

  isReturnPortalActive(): boolean {
    return this.returnPortal.active;
  }

  getTownPortalPosition(): { x: number; y: number } {
    return { x: this.townPortal.x, y: this.townPortal.y };
  }

  getReturnPortalPosition(): { x: number; y: number } {
    return { x: this.returnPortal.x, y: this.returnPortal.y };
  }

  getTownPortalDestination(): string {
    return this.townPortal.destination;
  }

  getReturnPortalDestination(): string {
    return this.returnPortal.destination;
  }

  // Check if player is near town portal
  isPlayerNearTownPortal(playerX: number, playerY: number, threshold: number = 50): boolean {
    if (!this.townPortal.active) return false;

    const dx = playerX - this.townPortal.x;
    const dy = playerY - this.townPortal.y;
    return Math.sqrt(dx * dx + dy * dy) < threshold;
  }

  // Check if player is near return portal
  isPlayerNearReturnPortal(playerX: number, playerY: number, threshold: number = 50): boolean {
    if (!this.returnPortal.active) return false;

    const dx = playerX - this.returnPortal.x;
    const dy = playerY - this.returnPortal.y;
    return Math.sqrt(dx * dx + dy * dy) < threshold;
  }

  update(deltaTime: number): void {
    if (this.townPortal.active) {
      this.townPortal.animationTime += deltaTime * this.ANIMATION_SPEED;
    }
    if (this.returnPortal.active) {
      this.returnPortal.animationTime += deltaTime * this.ANIMATION_SPEED;
    }
  }

  render(ctx: CanvasRenderingContext2D, cameraX: number, cameraY: number): void {
    if (this.townPortal.active) {
      this.renderPortal(ctx, this.townPortal, cameraX, cameraY, 'town');
    }
    if (this.returnPortal.active) {
      this.renderPortal(ctx, this.returnPortal, cameraX, cameraY, 'return');
    }
  }

  private renderPortal(
    ctx: CanvasRenderingContext2D,
    portal: PortalState,
    cameraX: number,
    cameraY: number,
    type: PortalType
  ): void {
    const screenX = portal.x - cameraX;
    const screenY = portal.y - cameraY;
    const time = portal.animationTime;

    // Different colors for town vs return portal
    const isTown = type === 'town';
    const primaryColor = isTown ? { r: 100, g: 150, b: 255 } : { r: 255, g: 150, b: 50 };
    const glowColor = isTown ? { r: 50, g: 100, b: 200 } : { r: 200, g: 100, b: 30 };
    const coreColor = isTown ? '#4a90d9' : '#d97a4a';
    const labelColor = isTown ? '#4a90d9' : '#d97a4a';

    // Portal glow effect
    const glowRadius = this.PORTAL_RADIUS + Math.sin(time * 3) * 5;

    // Outer glow
    const outerGradient = ctx.createRadialGradient(
      screenX, screenY, 0,
      screenX, screenY, glowRadius + 20
    );
    outerGradient.addColorStop(0, `rgba(${primaryColor.r}, ${primaryColor.g}, ${primaryColor.b}, 0.3)`);
    outerGradient.addColorStop(0.5, `rgba(${glowColor.r}, ${glowColor.g}, ${glowColor.b}, 0.2)`);
    outerGradient.addColorStop(1, `rgba(${glowColor.r}, ${glowColor.g}, ${glowColor.b}, 0)`);
    ctx.fillStyle = outerGradient;
    ctx.beginPath();
    ctx.arc(screenX, screenY, glowRadius + 20, 0, Math.PI * 2);
    ctx.fill();

    // Main portal circle
    const portalGradient = ctx.createRadialGradient(
      screenX, screenY, 0,
      screenX, screenY, glowRadius
    );
    portalGradient.addColorStop(0, `rgba(${primaryColor.r + 50}, ${primaryColor.g + 50}, ${primaryColor.b}, 0.9)`);
    portalGradient.addColorStop(0.3, `rgba(${primaryColor.r}, ${primaryColor.g}, ${primaryColor.b}, 0.7)`);
    portalGradient.addColorStop(0.7, `rgba(${glowColor.r}, ${glowColor.g}, ${glowColor.b}, 0.5)`);
    portalGradient.addColorStop(1, `rgba(${glowColor.r - 20}, ${glowColor.g - 30}, ${glowColor.b - 50}, 0.3)`);
    ctx.fillStyle = portalGradient;
    ctx.beginPath();
    ctx.arc(screenX, screenY, glowRadius, 0, Math.PI * 2);
    ctx.fill();

    // Swirl effect
    ctx.strokeStyle = `rgba(${primaryColor.r + 100}, ${primaryColor.g + 80}, ${primaryColor.b}, 0.6)`;
    ctx.lineWidth = 2;
    for (let i = 0; i < 3; i++) {
      const startAngle = time * 2 + (i * Math.PI * 2 / 3);
      const endAngle = startAngle + Math.PI * 1.5;
      const swirlRadius = (glowRadius * 0.3) + (i * glowRadius * 0.2);

      ctx.beginPath();
      ctx.arc(screenX, screenY, swirlRadius, startAngle, endAngle);
      ctx.stroke();
    }

    // Inner bright core
    const coreGradient = ctx.createRadialGradient(
      screenX, screenY, 0,
      screenX, screenY, glowRadius * 0.3
    );
    coreGradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
    coreGradient.addColorStop(1, `rgba(${primaryColor.r + 100}, ${primaryColor.g + 80}, ${primaryColor.b}, 0)`);
    ctx.fillStyle = coreGradient;
    ctx.beginPath();
    ctx.arc(screenX, screenY, glowRadius * 0.3, 0, Math.PI * 2);
    ctx.fill();

    // Portal ring
    ctx.strokeStyle = coreColor;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(screenX, screenY, glowRadius, 0, Math.PI * 2);
    ctx.stroke();

    // Floating particles
    for (let i = 0; i < 8; i++) {
      const angle = time + (i * Math.PI / 4);
      const dist = glowRadius * 0.7 + Math.sin(time * 2 + i) * 10;
      const px = screenX + Math.cos(angle) * dist;
      const py = screenY + Math.sin(angle) * dist - Math.sin(time * 3 + i) * 5;
      const particleSize = 2 + Math.sin(time * 4 + i) * 1;

      ctx.fillStyle = `rgba(${primaryColor.r + 100}, ${primaryColor.g + 80}, ${primaryColor.b}, 0.8)`;
      ctx.beginPath();
      ctx.arc(px, py, particleSize, 0, Math.PI * 2);
      ctx.fill();
    }

    // Label
    ctx.fillStyle = labelColor;
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    const label = isTown ? 'Kasaba Portali' : 'Donus Portali';
    ctx.fillText(label, screenX, screenY + glowRadius + 20);

    ctx.fillStyle = isTown ? '#8ac' : '#ca8';
    ctx.font = '10px Arial';
    ctx.fillText('[Tikla] Isinlan', screenX, screenY + glowRadius + 35);
  }
}
